import React from "react";
import { getTimelineSteps } from "./utils/stepDefinitions";
import { getAvailableScenarios } from "../services/mock/applicationData";
import { ErrorBoundary } from "./components/ui";
import { DashboardDebugPanel } from "./components/dev";
import DashboardLayout from "./components/layout/DashboardLayout";
import DashboardHeader from "./components/layout/DashboardHeader";
import ScenarioSelector from "./components/layout/ScenarioSelector";
import StudentDashboardRenderer from "./components/StudentDashboardRenderer";
import useRoleAuthentication from "../Authentication/hooks/useRoleAuthentication";
import {
  useStudentDashboardData,
  useStudentDashboardActions,
  useStudentDashboardAuth,
  useStudentDashboardState,
  useProcessedApplicationData,
  useStepNavigation
} from "./hooks";

/**
 * Student Dashboard Content Component
 * Enhanced version with configurable messaging and template-based rendering
 */
function StudentDashboardContent() {
  // Initialize state management
  const { showPreferencesForm, setShowPreferencesForm } = useStudentDashboardState();

  // Use custom hooks for data and actions first to check admin mode
  const {
    data: applicationData,
    isLoading: dataLoading,
    isError,
    error,
    refetch,
    isDevelopment,
    devScenario,
    isAdminOverride
  } = useStudentDashboardData();

  // Authentication check - skip if in admin mode
  const {
    user,
    roles,
    authLoading,
    isAuthenticated
  } = useStudentDashboardAuth(isAdminOverride);

  // Get admin role information for scenario selector access
  const { isAdmin } = useRoleAuthentication({ redirectOnUnauthorized: false });

  // Actions handler
  const { handleStepAction, handleScenarioChange } = useStudentDashboardActions(refetch, setShowPreferencesForm);

  // Process application data based on preferences form state
  const processedApplicationData = useProcessedApplicationData(applicationData, showPreferencesForm);

  // Use step navigation hook for enhanced step management (pass showPreferencesForm state)
  const stepNavigation = useStepNavigation(processedApplicationData, showPreferencesForm);

  // Generate steps with the processed data
  const timelineSteps = getTimelineSteps(processedApplicationData);

  // Get available scenarios for testing
  const availableScenarios = getAvailableScenarios();

  const isLoading = authLoading || dataLoading;



  // Check if scenario selector should be available
  // Requires BOTH environment variable AND admin role
  const canAccessScenarios = isDevelopment && isAdmin;

  // Create scenario selector component if both conditions are met
  const scenarioSelector = canAccessScenarios ? (
    <ScenarioSelector
      currentScenario={devScenario}
      availableScenarios={availableScenarios}
      onScenarioChange={handleScenarioChange}
    />
  ) : null;

  // Create debug panel only when test scenarios are enabled AND user is admin
  const debugPanel = canAccessScenarios ? (
    <DashboardDebugPanel
      applicationData={applicationData}
      currentScenario={devScenario}
      onScenarioChange={handleScenarioChange}
      timelineSteps={timelineSteps}
      stepNavigation={stepNavigation}
    />
  ) : null;

  return (
    <DashboardLayout>
      <DashboardHeader
        user={user}
        isAdminOverride={isAdminOverride}
        scenarioSelector={scenarioSelector}
      />

      <StudentDashboardRenderer
        // Authentication state
        isAuthenticated={isAuthenticated}
        authLoading={authLoading}

        // Data state
        applicationData={applicationData}
        processedApplicationData={processedApplicationData}
        isLoading={isLoading}
        isError={isError}
        error={error}

        // Timeline and navigation
        timelineSteps={timelineSteps}
        stepNavigation={stepNavigation}

        // Actions
        onStepAction={handleStepAction}
        onRefresh={refetch}

        // Admin and debug
        isAdminOverride={isAdminOverride}
        roles={roles}
        debugPanel={debugPanel}
      />
    </DashboardLayout>
  );
}

/**
 * Main Student Dashboard component wrapped with Error Boundary
 */
function StudentDashboard() {
  return (
    <ErrorBoundary>
      <StudentDashboardContent />
    </ErrorBoundary>
  );
}

export default StudentDashboard;